import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

const euros = (cents) => (cents == null ? "—" : `€${(cents / 100).toLocaleString()}`);

const Flag = ({ ok, label, testid }) => (
  <div className="flex items-center gap-2 text-xs" data-testid={testid}>
    {ok ? (
      <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
    ) : (
      <Circle size={14} className="text-slate-600 shrink-0" />
    )}
    <span className={ok ? "text-slate-300" : "text-slate-500"}>{label}</span>
  </div>
);

export const AdminPaymentsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/payments/readiness`, authHeader())
      .then((res) => setData(res.data))
      .catch(() => toast.error("Could not read the payments status"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  if (loading && !data) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 p-6 flex items-center gap-3 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking Stripe…
      </div>
    );
  }
  if (!data) return null;

  const account = data.account || {};
  const live = data.live;

  return (
    <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="payments-readiness-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <h2 className="font-serif text-xl text-white flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#D4AF37]" />
          Payments readiness
        </h2>
        <div className="flex items-center gap-3">
          <span
            className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1 ${
              live
                ? "border-emerald-700/60 text-emerald-300 bg-emerald-950/30"
                : "border-amber-700/60 text-amber-300 bg-amber-950/30"
            }`}
            data-testid="payments-mode"
          >
            {live ? "Live mode" : `${data.mode} mode`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            data-testid="payments-refresh"
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">
        {live
          ? "Real cards are being charged and money lands in your own Stripe balance."
          : "Cards are still test cards. Claim the Stripe sandbox from Emergent → Manage panel → Payments, then re-run this check."}
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-slate-800 bg-slate-950/60 p-5" data-testid="payments-account">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-4">Stripe account</p>
          <p className="text-white text-sm mb-1">{account.business_name || data.account_id || "—"}</p>
          <p className="text-slate-500 text-xs mb-4">
            {account.id || "unknown account"}
            {account.country ? ` · ${account.country}` : ""}
            {account.default_currency ? ` · ${account.default_currency}` : ""}
          </p>
          <div className="space-y-2">
            <Flag ok={!!account.details_submitted} label="Verification details submitted" testid="payments-flag-details" />
            <Flag ok={!!account.charges_enabled} label="Charges enabled" testid="payments-flag-charges" />
            <Flag ok={!!account.payouts_enabled} label="Payouts enabled" testid="payments-flag-payouts" />
            <Flag ok={!!data.webhook_secret_set} label="Webhook signing secret set" testid="payments-flag-webhook" />
          </div>
          {data.account_error && (
            <p className="text-amber-300 text-xs mt-4" data-testid="payments-account-error">
              {data.account_error}
            </p>
          )}
        </div>

        <div className="border border-slate-800 bg-slate-950/60 p-5" data-testid="payments-prices">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">Prices in this account</p>
          <p className="text-slate-500 text-[11px] mb-4">
            {data.prices_found} of {data.prices.length} found
          </p>
          <div className="space-y-2">
            {data.prices.map((price) => (
              <div
                key={price.lookup_key}
                className="flex items-center justify-between gap-3"
                data-testid={`payments-price-${price.lookup_key}`}
              >
                <span className="text-slate-300 text-xs truncate">
                  {price.label}
                  {price.interval ? <span className="text-slate-600"> /{price.interval}</span> : null}
                </span>
                <span className="text-xs whitespace-nowrap">
                  {price.found ? (
                    <span className={price.matches_expected ? "text-emerald-400" : "text-amber-400"}>
                      {euros(price.unit_amount)} {(price.currency || "").toUpperCase()}
                      {price.matches_expected ? "" : " ≠ expected"}
                    </span>
                  ) : (
                    <span className="text-red-400">missing</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {data.ready ? (
        <p
          className="mt-6 border border-emerald-800/60 bg-emerald-950/20 text-emerald-200 text-sm px-4 py-3 inline-flex items-center gap-2"
          data-testid="payments-ready"
        >
          <CheckCircle2 size={15} />
          Everything is in place — you can take real payments.
        </p>
      ) : (
        <div className="mt-6 space-y-2" data-testid="payments-blockers">
          {data.blockers.map((blocker, i) => (
            <p
              key={blocker}
              className="border border-amber-800/60 bg-amber-950/20 text-amber-200 text-xs px-4 py-3 flex items-start gap-2"
              data-testid={`payments-blocker-${i}`}
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              {blocker}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
